import { useContext, useLayoutEffect, useMemo, useState } from "react";
import useAuth from "../../../../../Hooks/useAuth";
import CustomizerContext from "../../../../../_helper/Customizer";
import {
    Description,
    ExpenseCategory,
    ExpenseGroup,
    ExpenseClass,
    ExpenseType,
    Name,
    Status,
    ModifiedBy,
    CreatedBy,
} from "../../../../../Constant";
import {
    financesExpenseCategoryApi,
    financesExpenseClassApi,
    financesExpenseGroupApi,
    financesExpenseTypeApi,
} from "../../../../../api";
import { createDropdownField } from "../../../../../Data/FormField/PublicData";
import useAxiosPrivate from "../../../../../Hooks/useAxiosPrivate";
import { ExpenseTypeResponse } from "../ExpenseType.types";
import { toast } from "react-toastify";
import { StatusRow } from "../../ReportType/List/ReportTypeList";
import { formatDateWithTimeZone } from "../../../../../_helper/dateHelper";

export default function useExpenseTypeListController() {
    const { userOrganisationId }: any = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const title = ExpenseType;
    const [formFields, setFormFields] = useState<any[]>([
        {
            id: "name",
            name: "name",
            label: Name,
            type: "text",
            value: "",
            required: true,
            colSize: 6,
        },

        {
            id: "description",
            name: "description",
            label: Description,
            type: "textarea",
            value: "",
            required: true,
            colSize: 6,
        },
        {
            id: "is_active",
            name: "is_active",
            label: Status,
            type: "switch",
            value: "1",
            colSize: 6,
        },
    ]);

    //?? DROPDOWNS INITS >
    const expenseCategoryDropDown = createDropdownField(
        axiosPrivate,
        "expense_category",
        "expense_category",
        ExpenseCategory,
        `${financesExpenseCategoryApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`,
        "",
        6
    );
    expenseCategoryDropDown.onChange = async (id, updatedFields) => {
        const nextField = updatedFields.find(
            (field: any) => field.id === "expense_group"
        );
        if (nextField) {
            const endPoint = `${financesExpenseGroupApi}?organisation_id=${userOrganisationId}&expense_category_id=${id}`
            await nextField.fetchOptions(id, false, endPoint);
            const updatedFormFields = updatedFields.map((field: any) => {
                if (field.id === "expense_category") {
                    return { ...field, value: id };
                }
                return field;
            });
            setFormFields([...updatedFormFields]);
        }
    };

    const expenseGroupDropDown = createDropdownField(
        axiosPrivate,
        "expense_group",
        "expense_group",
        ExpenseGroup,
        ``,
        "",
        6
    );
    expenseGroupDropDown.onChange = async (id, updatedFields) => {
        const nextField = updatedFields.find(
            (field: any) => field.id === "expense_class_id"
        );
        if (nextField) {
            const endPoint = `${financesExpenseClassApi}?organisation_id=${userOrganisationId}&expense_group_id=${id}`
            await nextField.fetchOptions(id, false, endPoint);
            const updatedFormFields = updatedFields.map((field: any) => {
                if (field.id === "expense_group") {
                    return { ...field, value: id };
                }
                return field;
            });
            setFormFields([...updatedFormFields]);
        }
    };

    const expenseClassDropDown = createDropdownField(
        axiosPrivate,
        "expense_class_id",
        "expense_class_id",
        ExpenseClass,
        `${financesExpenseClassApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`,
        "",
        6
    );

    useLayoutEffect(() => {
        //?? Updates all fields once component is mounted
        (async function () {
            const updatedFields = [
                expenseCategoryDropDown,
                expenseGroupDropDown,
                expenseClassDropDown,
                ...formFields,
            ];
            await expenseCategoryDropDown.fetchOptions();
            setFormFields(updatedFields);
        })();
    }, []);

    const tableColumns = [
        {
            name: Name,
            selector: (row: (typeof data)[0]) => `${row.name}`,
            sortable: true,
            center: false,
        },
        {
            name: ExpenseClass,
            selector: (row: (typeof data)[0]) => `${row.expense_class.name}`,
            sortable: true,
            center: false,
        },
        {
            name: ExpenseGroup,
            selector: (row: (typeof data)[0]) =>
                `${row.expense_class.expense_group.name}`,
            sortable: true,
            center: false,
        },
        {
            name: ExpenseCategory,
            selector: (row: (typeof data)[0]) =>
                `${row.expense_class.expense_group.expense_category.name}`,
            sortable: true,
            center: false,
        },
        {
            name: Status,
            selector: (row: (typeof data)[0]) => `${row.is_active}`,
            sortable: true,
            center: true,
            cell: (row: (typeof data)[0]) => StatusRow(row),
        },
        {
            name: CreatedBy,
            selector: (row: (typeof data)[0]) =>
                `${formatDateWithTimeZone(row.created_at)}`,
            sortable: false,
            center: false,
        },

        {
            name: ModifiedBy,
            selector: (row: (typeof data)[0]) =>
                `${formatDateWithTimeZone(row.updated_at)}`,
            sortable: false,
            center: false,
        },
    ];

    const handleEdit = (row: (typeof data)[0] | any) => {
        setViewModalOpen(true);
        setEditMode(true);

        row.expense_category =
            row.expense_class.expense_group.expense_category.id;
        expenseCategoryDropDown.onChange(row.expense_category, formFields);
        
        row.expense_group = row.expense_class.expense_group.id;
        expenseGroupDropDown.onChange(row.expense_group, formFields);

        row.expense_class_id = row.expense_class.id;
        expenseClassDropDown.onChange(row.expense_class_id, formFields);

        setSelectedRowData(row);
    };

    const { layoutURL } = useContext(CustomizerContext);
    const newBtn = `${process.env.PUBLIC_URL}/finance/settings/expense-type/create/${layoutURL}`;
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ExpenseTypeResponse[]>([]);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<
        (typeof data)[0] | null
    >(null);
    const [editMode, setEditMode] = useState(false);

    const fetchData = () => {
        setLoading(true);
        axiosPrivate
            .get(
                `${financesExpenseTypeApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`
            )
            .then((res) => {
                const response: ExpenseTypeResponse[] = res.data;
                setData(response);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 404) {
                  toast.error(err.response.data.detail);
                } else {
                  toast.error('Something Went Wrong');
                }
              })
            .finally(() => {
                setLoading(false);
            });
    };

    useLayoutEffect(() => {
        fetchData();
    }, []);

    const handleView = (row: (typeof data)[0]) => {
        setViewModalOpen(true);
        setEditMode(false);
        setSelectedRowData(row);
    };

    const handleEditRec = async (
        id: number | string,
        editRecord: (typeof data)[0] | any
    ) => {
        editRecord.expense_class = editRecord.expense_class_id;
        axiosPrivate
            .put(`${financesExpenseTypeApi}/${id}`, editRecord)
            .then((_res) => {
                const updatedData = data.map((row) =>
                    row.id === selectedRowData!.id ? { ...row, ..._res.data } : row
                );
                setData(updatedData);
                toast.success(`${_res.data.name} Editted Successfully.`)
                //fetchData();
            })
            .catch((err) => {
              if (err.response && err.response.status === 422) {
                toast.error('Validation Errors');
              } else if (err.response && err.response.status === 400) {
                toast.error(err.response.data.detail);
              } else {
                toast.error('Something Went Wrong');
              }
              throw err;
              //toast.error(err.response.message || "Whoops! Something went wrong");
            });
    };

    const memoizedHandleEdit = useMemo(
        () => handleEditRec,
        [data, selectedRowData]
    );

    return {
        data,
        tableColumns,
        viewModalOpen,
        selectedRowData,
        editMode,
        newBtn,
        title,
        formFields,
        loading,
        setViewModalOpen,
        handleView,
        handleEdit,
        memoizedHandleEdit,
    };
}
